import type { GuidedNotePreset } from './useEncounterTabConfig';

// ─── Types ───

export interface GuidedNoteCard {
  /** Unique card id */
  id: string;
  /** Display title for the card header */
  title: string;
  /** Icon name (lucide-style, matches ACCORDION_ICONS) */
  icon: string;
  /**
   * Submodule keys rendered inside this card.
   * A card with multiple keys is a presentation-layer grouping only —
   * each submodule keeps its own data/business logic.
   */
  submoduleKeys: string[];
  /**
   * When true, this section gets richer visual treatment (subtle background tint).
   * When false/omitted, the section renders as a lightweight document-like block.
   */
  emphasized?: boolean;
  /**
   * When true, the renderer should use internal pill/tab navigation
   * instead of stacking submodules vertically. Used for hero sections
   * like "Charting & Images" that have their own internal workspace.
   */
  hero?: boolean;
}

export interface GuidedNotePresetConfig {
  label: string;
  description: string;
  cards: GuidedNoteCard[];
}

// ─── Preset definitions ───

const GENERAL_PRESET: GuidedNotePresetConfig = {
  label: 'General',
  description: 'Standard clinical visit flow',
  cards: [
    { id: 'summary', title: 'Summary', icon: 'layout-grid', submoduleKeys: ['summary'] },
    // PRD.233 — Chief Complaint as a first-class card right after Summary.
    {
      id: 'chief-complaint',
      title: 'Chief Complaint',
      icon: 'message-square',
      submoduleKeys: ['chief_complaint'],
    },
    { id: 'vitals', title: 'Vitals', icon: 'heart', submoduleKeys: ['vitals'] },
    // PRD.214 — Symptoms structured chip entry (replaces "Chief Complaint & Symptoms" notes-style card)
    { id: 'symptoms', title: 'Symptoms', icon: 'clipboard', submoduleKeys: ['symptoms'] },
    // PRD.214 — Diagnosis structured chip entry
    { id: 'diagnosis', title: 'Diagnosis', icon: 'stethoscope', submoduleKeys: ['diagnoses'] },
    {
      id: 'notes',
      title: 'Notes',
      icon: 'file-text',
      submoduleKeys: ['notes'],
    },
    {
      id: 'prescriptions',
      title: 'Prescriptions',
      icon: 'clipboard',
      submoduleKeys: ['prescriptions'],
    },
    {
      id: 'forms-files-billing',
      title: 'Forms & Files',
      icon: 'folder-open',
      submoduleKeys: ['forms', 'files'],
    },
  ],
};

const DENTAL_PRESET: GuidedNotePresetConfig = {
  label: 'Dental',
  description: 'Dental-focused visit with charting and treatment plan',
  cards: [
    { id: 'summary', title: 'Summary', icon: 'layout-grid', submoduleKeys: ['summary'] },
    // PRD.233
    {
      id: 'chief-complaint',
      title: 'Chief Complaint',
      icon: 'message-square',
      submoduleKeys: ['chief_complaint'],
    },
    {
      id: 'charting-images',
      title: 'Charting & Images',
      icon: 'edit',
      submoduleKeys: ['charting', 'files'],
      emphasized: true,
      /** Hero section: renderer should use internal pills instead of stacked submodules */
      hero: true,
    },
    {
      id: 'notes-findings',
      title: 'Notes & Findings',
      icon: 'file-text',
      submoduleKeys: ['notes'],
    },
    {
      id: 'treatment-plan',
      title: 'Treatment Plan',
      icon: 'clipboard',
      submoduleKeys: ['treatment_plan'],
      emphasized: true,
    },
    { id: 'forms-consent', title: 'Forms & Consent', icon: 'file-text', submoduleKeys: ['forms'] },
    {
      id: 'prescriptions',
      title: 'Prescriptions',
      icon: 'clipboard',
      submoduleKeys: ['prescriptions'],
    },
    // Invoices + Payments moved to header Billing chip + panel (PRD.163)
  ],
};

const AESTHETIC_PRESET: GuidedNotePresetConfig = {
  label: 'Aesthetic',
  description: 'Aesthetic/cosmetic visit with visual assessment',
  cards: [
    { id: 'summary', title: 'Summary', icon: 'layout-grid', submoduleKeys: ['summary'] },
    // PRD.233
    {
      id: 'chief-complaint',
      title: 'Chief Complaint',
      icon: 'message-square',
      submoduleKeys: ['chief_complaint'],
    },
    {
      id: 'visual-assessment',
      title: 'Visual Assessment',
      icon: 'camera',
      submoduleKeys: ['charting', 'files'],
      emphasized: true,
      hero: true,
    },
    // PRD.214 — Split into separate structured chip entries
    { id: 'symptoms', title: 'Symptoms', icon: 'clipboard', submoduleKeys: ['symptoms'] },
    { id: 'diagnosis', title: 'Diagnosis', icon: 'stethoscope', submoduleKeys: ['diagnoses'] },
    {
      id: 'treatment-plan',
      title: 'Treatment Plan',
      icon: 'clipboard',
      submoduleKeys: ['treatment_plan'],
      emphasized: true,
    },
    { id: 'forms-consent', title: 'Forms & Consent', icon: 'file-text', submoduleKeys: ['forms'] },
    {
      id: 'prescriptions',
      title: 'Prescriptions',
      icon: 'clipboard',
      submoduleKeys: ['prescriptions'],
    },
    // Invoices + Payments moved to header Billing chip + panel (PRD.163)
  ],
};

const CUSTOM_PRESET: GuidedNotePresetConfig = {
  label: 'Custom',
  description: 'All submodules in individual cards',
  cards: [
    { id: 'summary', title: 'Summary', icon: 'layout-grid', submoduleKeys: ['summary'] },
    // PRD.233
    {
      id: 'chief-complaint',
      title: 'Chief Complaint',
      icon: 'message-square',
      submoduleKeys: ['chief_complaint'],
    },
    { id: 'vitals', title: 'Vitals', icon: 'heart', submoduleKeys: ['vitals'] },
    { id: 'charting', title: 'Charting', icon: 'edit', submoduleKeys: ['charting'] },
    { id: 'symptoms', title: 'Symptoms', icon: 'clipboard', submoduleKeys: ['symptoms'] },
    { id: 'diagnoses', title: 'Diagnosis', icon: 'stethoscope', submoduleKeys: ['diagnoses'] },
    { id: 'notes', title: 'Notes', icon: 'file-text', submoduleKeys: ['notes'] },
    {
      id: 'prescriptions',
      title: 'Prescriptions',
      icon: 'clipboard',
      submoduleKeys: ['prescriptions'],
    },
    {
      id: 'treatment-plan',
      title: 'Treatment Plan',
      icon: 'clipboard',
      submoduleKeys: ['treatment_plan'],
    },
    { id: 'forms', title: 'Forms', icon: 'file-text', submoduleKeys: ['forms'] },
    { id: 'files', title: 'Files', icon: 'folder-open', submoduleKeys: ['files'] },
    { id: 'invoices', title: 'Invoices', icon: 'credit-card', submoduleKeys: ['invoices'] },
    { id: 'payments', title: 'Payments', icon: 'credit-card', submoduleKeys: ['payments'] },
  ],
};

// ─── Preset registry ───

export const GUIDED_NOTE_PRESETS: Record<GuidedNotePreset, GuidedNotePresetConfig> = {
  general: GENERAL_PRESET,
  dental: DENTAL_PRESET,
  aesthetic: AESTHETIC_PRESET,
  custom: CUSTOM_PRESET,
};

/**
 * Resolve a preset, filtering out cards whose submodule keys are all disabled/hidden.
 * This gracefully handles missing/disabled submodules.
 */
export function resolveGuidedNoteCards(
  preset: GuidedNotePreset,
  visibleKeys: Set<string>,
): GuidedNoteCard[] {
  const config = GUIDED_NOTE_PRESETS[preset];
  if (!config) return GUIDED_NOTE_PRESETS.general.cards;

  return config.cards.filter((card) => card.submoduleKeys.some((key) => visibleKeys.has(key)));
}

// ─── Flat sub-module ordering per guided-note preset ───
// Used by the settings drawer to reorder the sub-module toggle list
// so it matches the clinical workflow of the selected preset.

export const GUIDED_NOTE_TAB_ORDER: Record<GuidedNotePreset, string[]> = {
  general: [
    'summary',
    'chief_complaint', // PRD.233 — full-width section right after Summary.
    'vitals',
    'symptoms', // PRD.214
    'diagnoses', // PRD.214
    'notes',
    'prescriptions',
    'treatment_plan',
    'forms',
    'files',
    'invoices',
    'payments',
    'charting',
  ],
  dental: [
    'summary',
    'chief_complaint', // PRD.233
    'vitals',
    'charting',
    'files',
    'symptoms', // PRD.214
    'diagnoses', // PRD.214
    'notes',
    'treatment_plan',
    'forms',
    'prescriptions',
    'invoices',
    'payments',
  ],
  aesthetic: [
    'summary',
    'chief_complaint', // PRD.233
    'charting',
    'files',
    'symptoms', // PRD.214
    'diagnoses', // PRD.214
    'notes',
    'treatment_plan',
    'forms',
    'prescriptions',
    'invoices',
    'payments',
    'vitals',
  ],
  custom: [
    'summary',
    'chief_complaint', // PRD.233
    'vitals',
    'charting',
    'symptoms', // PRD.214
    'diagnoses', // PRD.214
    'notes',
    'prescriptions',
    'treatment_plan',
    'forms',
    'files',
    'invoices',
    'payments',
  ],
};

/**
 * Reorder a TabConfig[] to match the guided-note preset's intended workflow.
 * Tabs not in the preset order are appended at the end.
 */
export function reorderTabsForPreset(
  tabs: readonly { key: string; label: string; visible: boolean }[],
  preset: GuidedNotePreset,
): { key: string; label: string; visible: boolean }[] {
  const order = GUIDED_NOTE_TAB_ORDER[preset];
  if (!order) return [...tabs];

  const byKey = new Map(tabs.map((t) => [t.key, t]));
  const result: { key: string; label: string; visible: boolean }[] = [];

  for (const key of order) {
    const tab = byKey.get(key);
    if (tab) {
      result.push(tab);
      byKey.delete(key);
    }
  }
  // Append any remaining tabs not in the preset order
  for (const tab of byKey.values()) {
    result.push(tab);
  }
  return result;
}
